/**
 * CSV-import av leads (CRM-4.1). Ren funksjon uten nett: `parseLeadCsv(text)`
 * gir gyldige rader (formen import_leads forventer) og en feilrapport per linje.
 * Skilletegn (; eller ,) oppdages fra overskriftslinjen. Maks 2000 rader.
 */

export const MAX_LEAD_CSV_ROWS = 2000;

export interface LeadImportContact {
  full_name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
}

export interface LeadImportRow {
  organization_number: string | null;
  name: string;
  industry_code: string | null;
  industry_description: string | null;
  employees: number | null;
  revenue_nok: number | null;
  website: string | null;
  municipality: string | null;
  contacts: LeadImportContact[];
}

export interface LeadCsvError {
  /** Linjenummer i filen (overskriften er linje 1). 0 = gjelder hele filen. */
  line: number;
  message: string;
}

export interface LeadCsvResult {
  rows: LeadImportRow[];
  errors: LeadCsvError[];
  delimiter: ";" | ",";
  /** Antall dataliner som ble lest (uten tomme). */
  total: number;
}

type Field =
  | "organization_number" | "name" | "industry_code" | "industry_description" | "employees"
  | "revenue_nok" | "website" | "municipality"
  | "contact_name" | "contact_title" | "contact_email" | "contact_phone";

const ALIASES: Record<Field, string[]> = {
  organization_number: ["orgnr", "org.nr", "organisasjonsnummer", "organization_number", "orgnummer", "organisasjonsnr"],
  name: ["navn", "name", "firmanavn", "selskap", "selskapsnavn", "bedrift", "bedriftsnavn"],
  industry_code: ["bransjekode", "næringskode", "naeringskode", "industry_code"],
  industry_description: ["bransje", "næring", "naering", "industry", "industry_description"],
  employees: ["ansatte", "antall ansatte", "antallansatte", "employees"],
  revenue_nok: ["omsetning", "driftsinntekter", "revenue", "revenue_nok"],
  website: ["nettside", "hjemmeside", "web", "website", "url"],
  municipality: ["kommune", "sted", "poststed", "municipality"],
  contact_name: ["kontakt_navn", "kontaktperson", "kontakt", "contact_name", "kontaktnavn"],
  contact_title: ["kontakt_tittel", "tittel", "stilling", "contact_title", "kontakttittel"],
  contact_email: ["kontakt_epost", "epost", "e-post", "email", "contact_email", "kontaktepost"],
  contact_phone: ["kontakt_mobil", "mobil", "telefon", "tlf", "contact_phone", "kontaktmobil"],
};

/** Sammenligningsform: små bokstaver, bare bokstaver og tall (æøå beholdes). */
const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9æøå]/g, "");

const ALIAS_LOOKUP: Map<string, Field> = new Map(
  (Object.entries(ALIASES) as [Field, string[]][]).flatMap(([field, names]) => names.map((n) => [norm(n), field] as const)),
);

function detectDelimiter(headerLine: string): ";" | "," {
  let semi = 0;
  let comma = 0;
  let inQuotes = false;
  for (const ch of headerLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch === ";") semi++;
    else if (!inQuotes && ch === ",") comma++;
  }
  return semi >= comma && semi > 0 ? ";" : comma > 0 ? "," : ";";
}

/** RFC 4180-lignende parsing: anførselstegn, doble anførselstegn som escape, linjeskift i felt. */
function parseRecords(text: string, delimiter: string): { cells: string[]; line: number }[] {
  const records: { cells: string[]; line: number }[] = [];
  let cells: string[] = [];
  let cell = "";
  let inQuotes = false;
  let line = 1;
  let recordLine = 1;
  const pushRecord = () => {
    cells.push(cell);
    if (cells.some((c) => c.trim() !== "")) records.push({ cells, line: recordLine });
    cells = [];
    cell = "";
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        if (ch === "\n") line++;
        cell += ch;
      }
      continue;
    }
    if (ch === '"') inQuotes = true;
    else if (ch === delimiter) {
      cells.push(cell);
      cell = "";
    } else if (ch === "\r") {
      // CRLF: la \n avslutte linjen
    } else if (ch === "\n") {
      pushRecord();
      line++;
      recordLine = line;
    } else cell += ch;
  }
  if (cell !== "" || cells.length > 0) pushRecord();
  return records;
}

export function normalizeCsvPhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/[^\d+]/g, "");
  if (/^\d{8}$/.test(digits)) return `+47${digits}`;
  if (/^0047\d{8}$/.test(digits)) return `+${digits.slice(2)}`;
  if (/^47\d{8}$/.test(digits)) return `+${digits}`;
  return digits || null;
}

const parseNumber = (raw: string): number | null | "invalid" => {
  const t = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : "invalid";
};

/** Nettside til https-adresse: «firma.no» får https://, alt annet enn http(s) avvises (null). */
export function normalizeWebsite(raw: string): string | null {
  const t = raw.trim();
  if (/^https?:\/\/\S+$/i.test(t)) return t;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i.test(t)) return `https://${t}`;
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseLeadCsv(input: string): LeadCsvResult {
  const text = input.replace(/^\uFEFF/, "");
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = detectDelimiter(firstLine);
  const records = parseRecords(text, delimiter);
  const errors: LeadCsvError[] = [];

  if (records.length === 0) {
    return { rows: [], errors: [{ line: 0, message: "Filen er tom" }], delimiter, total: 0 };
  }

  const header = records[0].cells.map((h) => ALIAS_LOOKUP.get(norm(h)) ?? null);
  if (!header.includes("name")) {
    return {
      rows: [],
      errors: [{ line: 1, message: "Fant ingen kolonne for navn. Bruk overskriften «navn» (eller selskap, firmanavn)." }],
      delimiter,
      total: records.length - 1,
    };
  }

  const dataRecords = records.slice(1);
  if (dataRecords.length > MAX_LEAD_CSV_ROWS) {
    errors.push({ line: 0, message: `Maks ${MAX_LEAD_CSV_ROWS} rader per fil. Del filen i flere.` });
  }

  const rows: LeadImportRow[] = [];
  for (const rec of dataRecords.slice(0, MAX_LEAD_CSV_ROWS)) {
    const get = (field: Field): string => {
      const idx = header.indexOf(field);
      return idx >= 0 ? (rec.cells[idx] ?? "").trim() : "";
    };
    const problems: string[] = [];

    const name = get("name");
    if (!name) problems.push("navn mangler");

    const orgRaw = get("organization_number");
    let organization_number: string | null = null;
    if (orgRaw) {
      const digits = orgRaw.replace(/\D/g, "");
      if (digits.length === 9) organization_number = digits;
      else problems.push(`ugyldig organisasjonsnummer «${orgRaw}»`);
    }

    const employees = parseNumber(get("employees"));
    if (employees === "invalid" || (employees !== null && !Number.isInteger(employees))) {
      problems.push("antall ansatte må være et heltall");
    }
    const revenue = parseNumber(get("revenue_nok"));
    if (revenue === "invalid") problems.push("omsetning må være et tall");

    const websiteRaw = get("website");
    const website = websiteRaw ? normalizeWebsite(websiteRaw) : null;
    if (websiteRaw && !website) problems.push(`ugyldig nettside «${websiteRaw}»`);

    const email = get("contact_email");
    if (email && !EMAIL_RE.test(email)) problems.push(`ugyldig e-post «${email}»`);

    if (problems.length > 0) {
      errors.push({ line: rec.line, message: problems.join(", ") });
      continue;
    }

    const contactName = get("contact_name");
    rows.push({
      organization_number,
      name,
      industry_code: get("industry_code") || null,
      industry_description: get("industry_description") || null,
      employees: typeof employees === "number" ? employees : null,
      revenue_nok: revenue === "invalid" ? null : revenue,
      website,
      municipality: get("municipality") || null,
      contacts: contactName
        ? [{ full_name: contactName, title: get("contact_title") || null, email: email || null, phone: normalizeCsvPhone(get("contact_phone")) }]
        : [],
    });
  }

  return { rows, errors, delimiter, total: dataRecords.length };
}
